import { apiRequest, useAccount } from "@vojomo-core/shared";
import { useEffect, useMemo, useState } from "react";

interface WeeklyProgressData {
  topic_id: string;
  text: string;
  rows: Record<string, { progress_pct: string; notes: string; actions?: string }>;
  last_updated_by_name: string;
}

export type ProgressRows = Record<
  string,
  { progress_pct: string; notes: string; actions?: string }
>;

export const useWeeklyProgress = (weekDateStr: string) => {
  const [progressData, setProgressData] = useState<WeeklyProgressData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [progressRows, setProgressRows] = useState<ProgressRows>({});
  const { companyName } = useAccount();

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setProgressRows({});

    apiRequest<WeeklyProgressData>({
      subRoute: "roadmap",
      path: `/weekly_progress_data?week_date=${weekDateStr}`,
      method: "GET"
    }).then((data) => {
      if (cancelled) return;
      setProgressData(data);
      setProgressRows(data?.rows || {});
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [weekDateStr, companyName]);

  const parseTable = (rawString: string) => {
    if (!rawString) return { headers: [], rows: [], hasTable: false };

    // Convert markdown table to HTML if no HTML table is present
    const toHtml = (text: string): string => {
      if (text.includes("<table")) return text;
      const lines = text.split("\n").map((l) => l.trim());
      const tableStart = lines.findIndex(
        (l) => l.startsWith("|") && l.endsWith("|")
      );
      if (tableStart === -1) return text;
      const tableLines = [];
      for (let i = tableStart; i < lines.length; i++) {
        if (lines[i].startsWith("|") && lines[i].endsWith("|"))
          tableLines.push(lines[i]);
        else if (tableLines.length > 0) break;
      }
      const sepIdx = tableLines.findIndex((l) => /^\|[\s\-:|]+\|$/.test(l));
      if (sepIdx < 1) return text;
      const parseRow = (l: string) =>
        l
          .slice(1, -1)
          .split("|")
          .map((c) => c.trim());
      let html = "<table><thead>";
      tableLines.slice(0, sepIdx).forEach((hl) => {
        html +=
          "<tr>" + parseRow(hl).map((h) => `<th>${h}</th>`).join("") + "</tr>";
      });
      html += "</thead><tbody>";
      tableLines.slice(sepIdx + 1).forEach((dl) => {
        html +=
          "<tr>" + parseRow(dl).map((c) => `<td>${c}</td>`).join("") + "</tr>";
      });
      html += "</tbody></table>";
      return html;
    };

    const htmlString = toHtml(rawString);

    try {
      const stripHtml = (value: string) => {
        const div = document.createElement("div");
        div.innerHTML = value;
        return (div.textContent || "").trim();
      };

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, "text/html");
      const table = doc.querySelector("table");
      if (!table) return { headers: [], rows: [], hasTable: false };

      const headers = Array.from(table.querySelectorAll("thead th")).map(
        (th) => th.innerHTML
      );
      const bodyRows = Array.from(table.querySelectorAll("tbody tr"));
      const rows =
        bodyRows.length > 0
          ? bodyRows
          : Array.from(table.querySelectorAll("tr")).filter(
              (tr) => tr.querySelectorAll("td").length > 0
            );

      const rowData = rows.map((tr) =>
        Array.from(tr.querySelectorAll("td")).map((td) => td.innerHTML)
      );

      const fallbackHeaders =
        headers.length > 0
          ? headers
          : Array.from(table.querySelectorAll("tr th")).map(
              (th) => th.innerHTML
            );

      const nonEmptyRows = rowData.filter((cells) =>
        cells.some((cell) => stripHtml(cell) !== "")
      );

      let normalizedRows = nonEmptyRows;
      if (fallbackHeaders.length > 0 && rowData.length > 0) {
        const headerText = fallbackHeaders.map(stripHtml);
        const firstRowText = normalizedRows[0]?.map(stripHtml) ?? [];
        const isHeaderRow =
          firstRowText.length === headerText.length &&
          firstRowText.every((cell, idx) => cell === headerText[idx]);
        if (isHeaderRow) normalizedRows = normalizedRows.slice(1);
      }

      return { headers: fallbackHeaders, rows: normalizedRows, hasTable: true };
    } catch {
      return { headers: [], rows: [], hasTable: false };
    }
  };

  const progressTable = useMemo(() => {
    if (!progressData) return null;
    return parseTable(progressData.text);
  }, [progressData]);

  const getPlainText = (html: string) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return (div.textContent || "").trim();
  };

  const saveProgress = async (rows: ProgressRows) => {
    await apiRequest({
      subRoute: "roadmap",
      path: `/weekly_progress`,
      method: "POST",
      body: { week_date: weekDateStr, rows }
    });
  };

  return {
    isLoading,
    progressData,
    progressTable,
    progressRows,
    setProgressRows,
    getPlainText,
    saveProgress
  };
};
